import {
  compact,
  concat,
  find,
  flow,
  groupBy,
  includes,
  isEmpty,
  join,
  map,
  reject,
  set,
  size,
  uniq,
  without,
} from 'lodash/fp';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { populateExpandoServiceData, Section, VisibilitySensor, Wrapper } from '@ahmdigital/ui';
import { PRODUCT } from '@ahmdigital/constants';
import getParentProductId from '@ahmdigital/logic/lib/utils/get-parent-product-id';
import React, { useCallback, useEffect } from 'react';

import { CHOOSABLE_PRODUCTS, DRAWER_IDS } from '../../ahm-constants/ui';
import { EVENTS } from '../../analytics/google/constants';
import { ProductType } from '../../types/product';
import analytics from '../../analytics';
import constants from '../../ahm-constants';
import CustomerType from '../../types/customer';
import customiseSelectedChoosableProduct from './customise-selected-choosable-product';
import DrawerActions from '../../actions/drawer';
import getCustomerHasChanged from '../../utils/customer-has-changed';
import getFutureDate from '../../utils/get-future-date';
import getVariableExcessComparerProducts from './components/utils/get-variable-excess-comparer-products';
import hasChoosable60Products from '../../utils/has-choosable-60-products';
import manufactureBaseChoosableProduct from './manufacture-base-choosable-product';
import NeedsProductDataLoader from '../../components/needs-product-data-loader';
import productListingCommon from './product-listing-common';
import ProductListingHead from './components/head';
import ProductPanels from '../../elements/product-panels';
import selectChoosableComparerProductId from '../../selectors/select-choosable-comparer-product-id';
import selectCustomer from '../../selectors/select-customer';
import selectFilteredNeedsProductsByCustomer from '../../selectors/select-filtered-needs-products-by-customer';
import selectIsCombiningHospitalWithExtras from '../../selectors/select-is-combining-hospital-with-extras';
import selectNeedsProductType from '../../selectors/select-needs-product-type';
import selectNeedsType from '../../selectors/select-needs-type';
import styles from './styles.module.sass';

const { COMBINED_PRODUCT_COMPONENTS } = constants;

const getParentProductIds = ({ products }: { products: ProductType[] }) =>
  flow([
    map((product) => {
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'id' does not exist on type '{}'.
      const productId = product?.id;
      const softBundleHospitalId = COMBINED_PRODUCT_COMPONENTS?.[productId]?.hospital;
      const idToGetParent = softBundleHospitalId || productId;
      return getParentProductId(idToGetParent);
    }),
    uniq,
    compact,
  ])(products);

const getProductsWithoutChoosable60 = (comparerProducts: ProductType[]) =>
  reject(
    (product: ProductType) => includes(product.id, CHOOSABLE_PRODUCTS) && product.id !== PRODUCT.CHOOSABLE_50_TEETH,
    comparerProducts,
  );

type ProductListingPageNeedsProps = {
  actions: unknown;
  choosableComparerProductId: string;
  customer: CustomerType;
  isCombiningHospitalWithExtras?: boolean;
  match: unknown;
  needsProductType: unknown;
  needsType: unknown;
  prices: unknown;
  productIdsWithExcess?: Record<string, number[]>;
  products: ProductType[];
  useFutureEffectiveDate: boolean;
};

const ProductListingPageNeeds = ({
  actions,
  choosableComparerProductId,
  customer,
  isCombiningHospitalWithExtras,
  match,
  needsProductType,
  needsType,
  prices,
  productIdsWithExcess,
  products,
  useFutureEffectiveDate,
}: ProductListingPageNeedsProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Previously, the cart used to be cleared on mount of this component allowing the customer
    // to add a new hospital product. Now, if the customer has already been to an extras product
    // page and have an extras product in their cart, we want the extras product to remain in
    // the cart and allow the customer to bundle a hospital product with their chosen extras
    // product.
    if (!isCombiningHospitalWithExtras) {
      // @ts-expect-error - Automatic, Please fix when editing this file
      actions.cart.clearCart();
    }
    // @ts-expect-error - Automatic, Please fix when editing this file
  }, [actions.cart, isCombiningHospitalWithExtras]);

  const parentProductIds = getParentProductIds({ products });

  let comparerProducts = getVariableExcessComparerProducts({
    customer,
    parentProductIds,
    productIdsWithExcess,
    products,
  });

  const customerHasChanged = getCustomerHasChanged(customer, useFutureEffectiveDate);
  const productIds = map('id', comparerProducts);
  const productIdsHaveChanged = join('|', productIds);

  useEffect(() => {
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions.price.load(productIds, customer, { date: getFutureDate(useFutureEffectiveDate) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, productIdsHaveChanged, useFutureEffectiveDate]);

  // Analytics
  useEffect(() => {
    if (!isEmpty(prices)) {
      analytics.ecommerceGA4(EVENTS.ECOMMERCE_GA4.VIEW_ITEM_LIST, {
        customer,
        prices,
        products: comparerProducts,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const choosable60ProductIds = without([PRODUCT.CHOOSABLE_50_TEETH], CHOOSABLE_PRODUCTS);
  const productsExcludingChoosable60 = reject(
    (product) => includes(product.id, choosable60ProductIds),
    comparerProducts,
  );

  const onSelectBundleClick = useCallback(() => {
    DrawerActions.openDrawer(DRAWER_IDS.PICKER)(dispatch);
  }, [dispatch]);

  if (hasChoosable60Products(comparerProducts)) {
    const choosable60TeethEyes = find({ id: PRODUCT.CHOOSABLE_60_TEETH_EYES }, comparerProducts);
    // A choosable 60 product has been selected via the picker drawer
    if (choosableComparerProductId) {
      const customisedProduct = customiseSelectedChoosableProduct(
        find({ id: choosableComparerProductId }, comparerProducts),
        onSelectBundleClick,
      );
      // Keep the product rank the same so the choosable product doesn't change it's position in the comparer
      const stableRank = choosable60TeethEyes?.rank;
      comparerProducts = concat(productsExcludingChoosable60, set('rank', stableRank, customisedProduct));
    } else {
      // Overwrite the relevant product properties to create the impression of a
      // base Choosable 60 product.
      const manufacturedBaseProduct = manufactureBaseChoosableProduct(choosable60TeethEyes, onSelectBundleClick);
      comparerProducts = concat(productsExcludingChoosable60, manufacturedBaseProduct);
    }
  } else {
    comparerProducts = getProductsWithoutChoosable60(comparerProducts);
  }

  const expandoProducts = populateExpandoServiceData(comparerProducts);

  const productPanelProps = {
    ...{ customer, prices },
    comparerProducts: expandoProducts,
    isCombiningHospitalWithExtras,
    isPromo: true,
    isRankSorted: size(groupBy('type', comparerProducts)) === 1,
    match,
    needsProductType,
    needsType,
    productIdsWithExcess,
    productType: needsProductType,
  };

  return (
    <Wrapper size="extraExtraLarge" isFullWidth>
      {/* @ts-expect-error - Automatic, Please fix when editing this file */}
      <ProductListingHead needsType={needsType} productType={needsProductType} needsProductType={needsProductType} />
      <div className={styles.productComparisonPaneWrapper}>
        <div id={`needs-type-${needsType}`} className={styles.wrapper}>
          <Section variant={styles.productCarouselSection}>
            <VisibilitySensor payload={{ component: 'ProductListingPage', element: needsProductType }}>
              {/* @ts-expect-error - Automatic, Please fix when editing this file */}
              <ProductPanels {...productPanelProps} />
            </VisibilitySensor>
          </Section>
        </div>
      </div>
    </Wrapper>
  );
};

ProductListingPageNeeds.defaultProps = {
  ...productListingCommon.defaultProps,
  isCombiningHospitalWithExtras: false,
  productIdsWithExcess: null,
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapStateToProps = (state, props) => ({
  choosableComparerProductId: selectChoosableComparerProductId(state),
  customer: selectCustomer(state),
  isCombiningHospitalWithExtras: selectIsCombiningHospitalWithExtras(state),
  needsProductType: selectNeedsProductType(state, props),
  needsType: selectNeedsType(state, props),
  prices: state.price.data,
  products: selectFilteredNeedsProductsByCustomer(state, props),
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

export default compose(
  NeedsProductDataLoader,
  connect(mapStateToProps, productListingCommon.mapDispatchToProps),
)(ProductListingPageNeeds);

export { ProductListingPageNeeds as ProductListingPageNeedsForTest };
